import React, { useState, ReactNode, ReactElement } from 'react';
import { Modal, ConfigProvider } from 'antd';
import customizeTheme from 'components/customize-theme';
import Icons from 'components/icons';

interface IModalStandardProps
{
    modalTitle?: React.ReactNode;
    modalButton?: ReactElement;
    extraClass?: string;
    showModal?: boolean;
    setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
    maskClosable?: boolean;
    closable?: boolean;
    defaultShow?: boolean;
    disabled?: boolean;
    afterClose?: () => void;
    width?: number;
    onCancel?: () => void;
    children: ReactNode | ReactNode[];
}

const injectModalProps = (
    child: ReactNode,
    injectedProps: {
        action: () => void;
    }
) =>
{
    if (!React.isValidElement(child)) {
        return child;
    }

    if (typeof child.type === "string" || child.type === React.Fragment) {
        return child;
    }

    return React.cloneElement(child as ReactElement, injectedProps);
};

const ModalStandard: React.FC<IModalStandardProps> = ({
    modalTitle,
    modalButton,
    extraClass = '',
    showModal: parentShowModal,
    setShowModal: parentSetShowModal,
    maskClosable = true,
    closable = true,
    defaultShow = false,
    disabled = false,
    afterClose = () => { },
    width = 380,
    onCancel = () => { },
    children,
}) =>
{
    const [ showModal, setShowModal ] = useState<boolean>(defaultShow);

    const isControlled =
        typeof parentShowModal !== 'undefined' &&
        typeof parentSetShowModal !== 'undefined';

    const handleModalVisibility = (visible: boolean) =>
    {
        if (isControlled) {
            parentSetShowModal(visible);
        } else {
            setShowModal(visible);
        }
    };

    return (
        <>
            {modalButton &&
                React.cloneElement(modalButton, {
                    onClick: () =>
                    {
                        if (!disabled) {
                            handleModalVisibility(true);
                        }
                    },
                })}

            <Modal
                title={modalTitle}
                className={`modal-standard ${extraClass}`}
                centered
                open={isControlled ? parentShowModal : showModal}
                maskClosable={maskClosable}
                width={width}
                closable={closable}
                closeIcon={<Icons.Close />}
                destroyOnClose
                footer={null}
                onOk={() => handleModalVisibility(false)}
                onCancel={() =>
                {
                    handleModalVisibility(false);
                    onCancel();
                }}
                afterClose={() => afterClose()}
            >
                <ConfigProvider theme={customizeTheme.LightTheme()}>
                    {React.Children.map(children, (child) =>
                        injectModalProps(child, {
                            action: () => handleModalVisibility(false),
                        })
                    )}
                </ConfigProvider>
            </Modal>
        </>
    );
};

export default ModalStandard;
